import { withQueryparams } from "@/main/components/core/queryParams/queryparams";
import Button from "@/main/components/shared/button/button";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import { PartsCancelledSearchRequest } from "@/main/models/request";
import { PartsCancelledSearchResponse } from "@/main/models/response";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { ISearchCancelledPartsForm } from "@/main/screens/order/cancelledParts/cancelledPartsScreenUtils";
import { BackToTopButton } from "@/main/screens/order/cancelledParts/components/backToTopButton/backToTopButton";
import CancelledPartsGrid from "@/main/screens/order/cancelledParts/components/cancelledPartsGrid/cancelledPartsGrid";
import FiltersCancelledParts from "@/main/screens/order/cancelledParts/components/filtersCancelledParts/filtersCancelledParts";
import ModalReassignBodyshopPartsCancelled from "@/main/screens/order/cancelledParts/components/modalReassignBodyshopPartsCancelled/modalReassignBodyshopPartsCancelled";
import ModalRequotePartsCancelled from "@/main/screens/order/cancelledParts/components/modalRequotePartsCancelled/modalRequotePartsCancelled";
import { Controls } from "@/main/services/constants/constantsControl";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { isNullUndefined } from "@/main/services/reducerUtils";
import {
	saveSessionStorage,
	SELECTED_CANCELLED_PARTS_REASSIGN,
	SelectedCancelledPartsReassign,
} from "@/main/services/storage";
import { getTranslateByKey, isShowControlByElementId } from "@/main/services/utils";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as S from "./styles";

function CancelledPartsScreen() {
	const [filterRequest, setFilterRequest] = useState<PartsCancelledSearchRequest | null>(null);
	const [selectedParts, setSelectedParts] = useState<Set<string>>(new Set());
	const [dataGrid, setDataGrid] = useState<PartsCancelledSearchResponse[]>([]);
	const [requoteSelection, setRequoteSelection] = useState<SelectedCancelledPartsReassign | null>(null);

	const [reassignBodyshopSelection, setReassignBodyshopSelection] = useState<SelectedCancelledPartsReassign | null>(
		null,
	);

	const navigate = useNavigate();

	const maxSelect = useParameterByTypeScreenReducer(ParameterTypeEnum.PartsCancelledMaxSelect);

	const canSeeRequoteButton = isShowControlByElementId(Controls.IdManagePartsRequote);
	const canSeeReassignButton = isShowControlByElementId(Controls.IdManagePartsReassign);
	const canSeeReassignToBodyshopButton = isShowControlByElementId(Controls.IdManagePartsReassignToBodyshop);

	function handleChangeSelection(procurementSupplierItemGuid: string) {
		setSelectedParts((prev) => {
			const next = new Set(prev);
			if (next.has(procurementSupplierItemGuid)) {
				next.delete(procurementSupplierItemGuid);
			} else {
				next.add(procurementSupplierItemGuid);
			}
			return next;
		});
	}

	function handleSelectAll(selected: boolean) {
		if (selected) {
			const ids = dataGrid.map((item) => item.procurementSupplierItemGuid).slice(0, maxSelect);
			setSelectedParts(new Set(ids));
		} else {
			setSelectedParts(new Set());
		}
	}

	function handleAction(action: "requote" | "reassign" | "reassignBodyshop") {
		if (selectedParts.size === 0) {
			toast.error(getTranslateByKey("information.selectAtLeastOnePart"));
			return;
		}

		const procurementSupplierItemGuids = [...selectedParts];
		const quoteGuids = dataGrid
			.filter((part) => selectedParts.has(part.procurementSupplierItemGuid))
			.map((part) => part.quoteGuid);
		const guidCompanyBuyer = dataGrid.at(0)?.companyBuyerGuid ?? "";

		switch (action) {
			case "reassignBodyshop":
				setReassignBodyshopSelection({
					procurementSupplierItemGuids,
					quoteGuids,
					guidCompanyBuyer,
				});
				break;

			case "requote":
				setRequoteSelection({
					procurementSupplierItemGuids,
					quoteGuids,
					guidCompanyBuyer,
				});
				break;

			case "reassign":
				saveSessionStorage<SelectedCancelledPartsReassign>(SELECTED_CANCELLED_PARTS_REASSIGN, {
					procurementSupplierItemGuids,
					quoteGuids,
					guidCompanyBuyer: dataGrid.at(0)?.companyBuyerGuid ?? "",
					partsInfo: dataGrid
						.filter((part) => selectedParts.has(part.procurementSupplierItemGuid))
						.map((part) => ({
							procurementSupplierItemGuid: part.procurementSupplierItemGuid,
							claimNumber: part.claimNumber,
							procurementCode: part.procurementCode,
							quoteCode: part.quoteCode,
							insurerName: part.insurerName,
							vehicleFullName: part.vehicleFullName,
						})),
				});
				navigate(`${RoutesConstants.orderReassignOrderBulk}?cancelledParts=true`);
				break;
			default:
				break;
		}
	}

	function handleCloseModal(refreshGrid: boolean = false) {
		setRequoteSelection(null);
		setReassignBodyshopSelection(null);

		if (!refreshGrid) return;
		if (isNullUndefined(filterRequest)) return;
		setDataGrid([]);
		setSelectedParts(new Set());
		const request = {
			...filterRequest,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		};
		setFilterRequest(request);
	}

	function onResetSearch() {
		setDataGrid([]);
		setFilterRequest(null);
		setSelectedParts(new Set());
	}

	function handleSubmit(formData: ISearchCancelledPartsForm) {
		const request: PartsCancelledSearchRequest = {
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
			guidInsurer: String(formData.insurer?.key),
			bodyshopGuids: formData.bodyshop ? formData.bodyshop.map((bd) => String(bd.key)) : undefined,
			guidSupplier: formData.supplier ? String(formData.supplier.key) : undefined,
			licenseOrClaimNr: formData.claimNumber ?? undefined,
			guidStates: formData.deliveryAddressState
				? formData.deliveryAddressState.map((state) => String(state.key))
				: undefined,
			startDate: formData.startDate.toISOString(),
			finalDate: formData.finalDate.toISOString(),
			guidVehicleModel: formData.vehicleModel,
			guidVehicleManufacturer: formData.vehicleManufacturer,
			guidVehicleSubModel: formData.subModel,
		};

		setDataGrid([]);
		setSelectedParts(new Set());
		setFilterRequest(request);
	}

	return (
		<ContainerScreen
			style={{ minHeight: "90%" }}
			title={`
      ${getTranslateByKey("information.order")}
      ${" > "}
      ${getTranslateByKey("screen.managePartsCancelledBySupplier")}`}
		>
			<S.Container>
				<FiltersCancelledParts onSubmit={handleSubmit} onClear={onResetSearch} />

				<S.ButtonsWrapper>
					{canSeeRequoteButton && (
						<Button id="id-searchPartsCancelled-btn-requote" onClick={() => handleAction("requote")}>
							{getTranslateByKey("information.requote")}
						</Button>
					)}
					{canSeeReassignButton && (
						<Button id="id-searchPartsCancelled-btn-reassign" onClick={() => handleAction("reassign")}>
							{getTranslateByKey("information.reassign")}
						</Button>
					)}
					{canSeeReassignToBodyshopButton && (
						<Button id="id-searchPartsCancelled-btn-reassignBodyshop" onClick={() => handleAction("reassignBodyshop")}>
							{getTranslateByKey("information.reassignToBodyshop")}
						</Button>
					)}
				</S.ButtonsWrapper>

				<BackToTopButton />

				<CancelledPartsGrid
					dataGrid={dataGrid}
					filter={filterRequest}
					handleChangeSelection={handleChangeSelection}
					selectedParts={selectedParts}
					setDataGrid={setDataGrid}
					handleSelectAll={handleSelectAll}
					maxSelect={maxSelect}
				/>

				<ModalRequotePartsCancelled
					guidCompany={requoteSelection?.guidCompanyBuyer ?? ""}
					isOpen={requoteSelection !== null}
					procurementSupplierItemGuids={requoteSelection?.procurementSupplierItemGuids ?? []}
					quoteGuids={requoteSelection?.quoteGuids ?? []}
					handleClose={handleCloseModal}
				/>

				<ModalReassignBodyshopPartsCancelled
					guidCompany={reassignBodyshopSelection?.guidCompanyBuyer ?? ""}
					isOpen={reassignBodyshopSelection !== null}
					procurementSupplierItemGuids={reassignBodyshopSelection?.procurementSupplierItemGuids ?? []}
					quoteGuids={reassignBodyshopSelection?.quoteGuids ?? []}
					handleClose={handleCloseModal}
				/>
			</S.Container>
		</ContainerScreen>
	);
}

export default withQueryparams(CancelledPartsScreen);
